// Lead sources (migration 142) — where a company first came from, kept as
// three facts instead of one text field: the CHANNEL (a Settings catalog:
// workshop, lunch-and-learn, referral partner, inbound…), the EVENT (an
// instance of a channel — "Phoenix Lunch and Learn, Aug 2026"), and the
// PERSON who brought the owner (an attendee row, which fills the contact's
// referral block). The lead lives on the company and its deals inherit it.
//
// Rules the UI leans on (enforced server-side, app/lead_sources.py):
//   - the event wins: pick an event and its channel comes with it
//   - first touch sticks: fills are NULL → value only, never an overwrite
//   - the lead has its own date: stamped when captured, not the event's date
//
// Attached to window for the detail pages and Settings:
//   LeadSourcePill · LeadSourceFields · LeadEventDrawer (+ its invite
//   campaign: LeadEventInvites) · ContactLeadEventsPanel ·
//   LeadSourcesSettings (channels) · LeadEventsView (sidebar, + the
//   event-campaign detector) · LeadSourceNudge (+ saveLeadNudge)

const LEAD_GROUP_META = {
  referral: { label: 'Referral', hue: 'teal' },
  event:    { label: 'Event',    hue: 'violet' },
  inbound:  { label: 'Inbound',  hue: 'green' },
  outbound: { label: 'Outbound', hue: 'blue' },
  other:    { label: 'Other',    hue: 'slate' },
};
const LEAD_GROUP_OPTIONS = Object.entries(LEAD_GROUP_META).map(([value, m]) => ({ value, label: m.label }));

const ATTENDEE_STATUS_META = {
  attended:   { label: 'attended',   hue: 'green' },
  registered: { label: 'registered', hue: 'amber' },
  no_show:    { label: 'no-show',    hue: 'rose' },
};
const ATTENDEE_STATUS_OPTIONS = Object.entries(ATTENDEE_STATUS_META).map(([value, m]) => ({ value, label: m.label }));

const fmtLeadDate = (d) => (d ? (window.fmtDate ? window.fmtDate(d) : d) : '');
const eventDateLabel = (ev) => {
  if (!ev) return '';
  if (ev.starts_on && ev.ends_on && ev.ends_on !== ev.starts_on) return `${fmtLeadDate(ev.starts_on)} – ${fmtLeadDate(ev.ends_on)}`;
  return fmtLeadDate(ev.starts_on);
};
const eventPlace = (ev) => [ev && ev.city, ev && ev.state].filter(Boolean).join(', ');

const LeadSourcePill = ({ name, group, title }) => (
  <span title={title}>
    <Pill hue={(LEAD_GROUP_META[group] || LEAD_GROUP_META.other).hue} sz="sm">{name}</Pill>
  </span>
);

// The catalog, including retired rows so a stale value can still be named.
const useLeadSources = () => {
  const [sources, setSources] = React.useState([]);
  const reload = React.useCallback(() => {
    window.AtlasApi.listLeadSources({ include_inactive: true })
      .then((s) => setSources(Array.isArray(s) ? s : []))
      .catch(() => setSources([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  return [sources, reload];
};

// ── Event picker ───────────────────────────────────────────────────────────
// Server-searched combobox over events, narrowed to the record's channel when
// it has one, with a "New event…" row at the top so a dealmaker who just met
// owners at an event nobody has created yet doesn't leave to Settings.
const NEW_EVENT = '__new_event__';

const LeadEventPicker = ({ value, valueLabel, sourceId, onChange, onCreateNew, autoOpen }) => {
  const load = React.useCallback((q) => (
    window.AtlasApi.listLeadEvents({ q: q || undefined, lead_source_id: sourceId || undefined, limit: 30 })
      .then((rows) => (Array.isArray(rows) ? rows : []).map((ev) => ({
        value: ev.id,
        label: ev.name,
        hint: [ev.lead_source_name, eventDateLabel(ev)].filter(Boolean).join(' · '),
      })))
  ), [sourceId]);
  const statics = [
    { value: NEW_EVENT, label: '＋ New event…' },
    // Only offer "none" when there is something to clear — otherwise the
    // trigger would read "— none —" instead of inviting a search.
    ...(value ? [{ value: '', label: '— none —' }] : []),
    // Keep the current pick nameable even if the search never returns it.
    ...(value && valueLabel ? [{ value, label: valueLabel }] : []),
  ];
  return (
    <Combobox
      value={value || ''}
      options={statics}
      loadOptions={load}
      placeholder="Search events…"
      autoOpen={autoOpen}
      onChange={(e) => {
        const v = e.target.value;
        if (v === NEW_EVENT) { onCreateNew && onCreateNew(); return; }
        onChange(v || null, v ? e.label : '');
      }}
    />
  );
};

// Read/edit cell for the Event row — same hover-pencil affordance as
// EditableCell, but the editor is the searchable picker above.
// editable=false: the value alone (no pencil, no click).
const LeadEventCell = ({ raw, onSave, editable = true }) => {
  const [editing, setEditing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const commit = async (lead_event_id) => {
    if ((lead_event_id || null) === (raw.lead_event_id || null)) { setEditing(false); return; }
    setSaving(true);
    try { await onSave({ lead_event_id }); } catch (_) {}
    finally { setSaving(false); setEditing(false); }
  };

  const shown = raw.lead_event_id
    ? <Pill hue="violet" sz="sm">{raw.lead_event_name || 'Event'}</Pill>
    : <span style={{ color: 'var(--text-4)' }}>—</span>;
  if (!editable) {
    return <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown}</span>;
  }
  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <LeadEventPicker
            value={raw.lead_event_id || ''}
            valueLabel={raw.lead_event_name}
            sourceId={raw.lead_source_id}
            autoOpen
            onChange={commit}
            onCreateNew={() => setCreating(true)}
          />
        </div>
        <button onClick={() => setEditing(false)} title="Cancel"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 12, padding: 0 }}>✕</button>
        {creating && ReactDOM.createPortal(
          <LeadEventDrawer
            isNew
            presetSourceId={raw.lead_source_id || null}
            onClose={() => setCreating(false)}
            onSaved={(ev) => { setCreating(false); commit(ev.id); }}
            zIndex={70}
          />, document.body)}
      </div>
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; setHovered(true); }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; setHovered(false); }}
      title="Click to edit"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%', cursor: 'pointer',
               padding: '2px 4px', marginLeft: -4, borderRadius: 4, transition: 'background .1s' }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {shown}
      </span>
      {hovered && !saving && <span style={{ flexShrink: 0, display: 'inline-flex' }}><Icon.Edit size={10} color="var(--text-4)" /></span>}
      {saving && <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0 }}>saving…</span>}
    </span>
  );
};

// ── Detail-page rows ───────────────────────────────────────────────────────
// `Row` is the caller's own label/value row (company + deal pages each have
// one), so the block matches whatever panel it sits in. `raw` is the record
// as the API returned it (lead_source_id/_name/_group, lead_event_id/_name,
// lead_source_at, lead_source_detail). Companies pass withDate + withDetail;
// deals only carry channel + event (the lead date lives on the company).
// eventLabel: the contact page calls the event row "Lead event" (its own
// Events panel lists every event the person was at). editable=false renders
// every row read-only (a viewer on the v2 contact page).
const LeadSourceFields = ({ raw, onSave, Row, withDate = false, withDetail = false, eventLabel = 'Event', editable = true }) => {
  const [sources] = useLeadSources();
  const EditableCell = window.EditableCell;
  const opts = [
    { value: null, label: '— Not recorded —' },
    ...sources.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name })),
  ];
  return (
    <>
      <Row label="Lead source" value={
        <EditableCell type="select" value={raw.lead_source_id || null} options={opts} editable={editable}
          fallbackLabel={raw.lead_source_name}
          onSave={(lead_source_id) => onSave({ lead_source_id })}
          displayAs={raw.lead_source_id
            ? <LeadSourcePill name={raw.lead_source_name || 'Source'} group={raw.lead_source_group} />
            : undefined} />
      } />
      <Row label={eventLabel} value={<LeadEventCell raw={raw} onSave={onSave} editable={editable} />} />
      {withDate && (
        <Row label="Lead date" value={
          <EditableCell type="date" value={raw.lead_source_at || null} editable={editable}
            onSave={(lead_source_at) => onSave({ lead_source_at })} />
        } />
      )}
      {withDetail && (
        <Row label="Detail" value={
          <EditableCell type="text" value={raw.lead_source_detail || ''} placeholder="e.g. met at Rotary breakfast" editable={editable}
            onSave={(lead_source_detail) => onSave({ lead_source_detail })} />
        } />
      )}
    </>
  );
};

// ── Invite campaign (lead_event_campaigns) ─────────────────────────────────
// The SuperSend campaign that invited people to the event. Its sends and
// replies are read live; invitees are NOT attendees (an attendance would set
// every invited company's lead source), so a reply becomes "registered" only
// when someone clicks it.
const INTEREST_META = {
  interested:     { label: 'interested',     hue: 'green' },
  not_interested: { label: 'not interested', hue: 'rose' },
};

const LeadEventCampaignDrawer = ({ event, onClose, onLinked }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [pick, setPick] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.leadEventCampaignCandidates(event.id)
      .then((d) => { setData(d); if (d.suggested) setPick(d.suggested); })
      .catch((e) => setError(e.message || 'Could not list campaigns'));
  }, [event.id]);
  const chosen = data && data.items.find((c) => c.campaign_id === pick);
  const link = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const res = await window.AtlasApi.linkLeadEventCampaign(event.id, { campaign_id: chosen.campaign_id, campaign_name: chosen.name });
      pushToast({ type: 'success', title: 'Invite campaign linked', body: `${res.invited.toLocaleString()} invited so far` });
      onLinked && onLinked();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not link', body: e.message });
    } finally { setBusy(false); }
  };
  return (
    <Drawer open onClose={onClose} width={560} zIndex={70} title="Link invite campaign" subtitle={`SuperSend invite for ${event.name}`}
      footer={(
        <>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={!chosen || !!(chosen && chosen.linked_to) || busy} onClick={link}>
            {busy ? 'Linking…' : 'Link campaign'}
          </Btn>
        </>
      )}>
      {error && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{error}</div>}
      {!data && !error && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {data && !data.items.length && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No SuperSend campaign has sent recently.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data && data.items.map((c) => (
          <label key={c.campaign_id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8,
            border: `1px solid ${pick === c.campaign_id ? 'var(--accent)' : 'var(--border)'}`,
            background: pick === c.campaign_id ? 'var(--accent-soft)' : 'var(--surface)',
            cursor: c.linked_to ? 'not-allowed' : 'pointer', opacity: c.linked_to ? 0.6 : 1,
          }}>
            <input type="radio" name="event-campaign" disabled={!!c.linked_to} checked={pick === c.campaign_id}
              onChange={() => setPick(c.campaign_id)} style={{ accentColor: 'var(--accent)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{c.name || c.campaign_id}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {c.recipients.toLocaleString()} recipients · {fmtLeadDate(c.first_at)} – {fmtLeadDate(c.last_at)}
                {c.linked_to ? ` · linked to ${c.linked_to}` : ''}
              </div>
            </div>
            {data.suggested === c.campaign_id && <Pill hue="green" sz="sm">likely</Pill>}
          </label>
        ))}
      </div>
    </Drawer>
  );
};

const LeadEventInvites = ({ event, canEdit, onChanged }) => {
  const [data, setData] = React.useState(null);
  const [linking, setLinking] = React.useState(false);
  const [busy, setBusy] = React.useState(null);   // contact id being registered
  const reload = React.useCallback(() => {
    window.AtlasApi.leadEventCampaigns(event.id).then(setData).catch(() => setData({ campaigns: [], funnel: {}, replies: [] }));
  }, [event.id]);
  React.useEffect(() => { reload(); }, [reload]);

  const unlink = async (c) => {
    const ok = await showConfirm({
      title: `Unlink “${c.name || c.campaign_id}”?`,
      body: 'Registered people stay on the event; the invite numbers and replies just stop showing here.',
      confirmLabel: 'Unlink',
    });
    if (!ok) return;
    try {
      await window.AtlasApi.unlinkLeadEventCampaign(event.id, c.id);
      reload();
      onChanged && onChanged();
    } catch (e) { pushToast({ type: 'error', title: 'Could not unlink', body: e.message }); }
  };
  const register = async (r) => {
    setBusy(r.contact_id);
    try {
      await window.AtlasApi.upsertLeadEventAttendee(event.id, r.contact_id, { status: 'registered', stamp_company: true, record_referral: false });
      pushToast({ type: 'success', title: 'Registered', body: [r.name, r.company_name].filter(Boolean).join(' · ') });
      reload();
      onChanged && onChanged();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not register', body: e.message });
    } finally { setBusy(null); }
  };

  if (!data) return null;
  const f = data.funnel || {};
  const seats = (f.registered || 0) + (f.attended || 0) + (f.no_show || 0);
  const Stat = ({ label, value, title }) => (
    <div title={title} style={{ flex: 1, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }} className="num">{(value || 0).toLocaleString()}</div>
      <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Invite campaign</div>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <Btn size="xs" variant="ghost" onClick={() => setLinking(true)}>
            {data.campaigns.length ? '+ Link another' : '+ Link SuperSend campaign'}
          </Btn>
        )}
      </div>
      {!data.campaigns.length && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No SuperSend campaign linked.</div>
      )}
      {data.campaigns.length > 0 && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {data.campaigns.map((c) => (
              <span key={c.id} title={`Linked${c.linked_by ? ' by ' + c.linked_by : ''}${c.linked_at ? ' · ' + fmtLeadDate(c.linked_at) : ''}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11.5,
                         border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                <Icon.Mail size={10} /> {c.name || c.campaign_id}
                {canEdit && (
                  <button onClick={() => unlink(c)} title="Unlink this campaign"
                    style={{ background: 'none', border: 'none', padding: 0, marginLeft: 2, cursor: 'pointer', color: 'var(--text-4)', lineHeight: 1 }}>×</button>
                )}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Stat label="Invited" value={f.invited} title="People the campaign has emailed" />
            <Stat label="Replied" value={f.replied} title="Out-of-office auto-replies not counted" />
            <Stat label="Interested" value={f.interested} title="SuperSend's read of the reply" />
            <Stat label="Registered" value={seats} title="People on the event: registered, attended or no-show" />
          </div>
          {data.replies.length > 0 && (
            <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {data.replies.map((r) => {
                const im = INTEREST_META[r.interest];
                const st = r.attendee_status && ATTENDEE_STATUS_META[r.attendee_status];
                return (
                  <div key={r.contact_id || r.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', minWidth: 0 }}>
                        {r.contact_id ? (
                          <span role="button" style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap' }}
                            onClick={() => window.AtlasNav?.goToContact && window.AtlasNav.goToContact(r.contact_id)}>{r.name || r.email || 'Contact'}</span>
                        ) : (
                          <span style={{ color: 'var(--text-2)' }} title="No Atlas contact has this address yet">{r.email}</span>
                        )}
                        {r.company_name && <span style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {r.company_name}</span>}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
                        {r.replied_at ? `replied ${fmtLeadDate(r.replied_at)}` : 'classified without a reply'}{r.unsubscribed ? ' · unsubscribed' : ''}
                      </div>
                    </div>
                    {im ? <Pill hue={im.hue} sz="sm">{im.label}</Pill> : <span title="SuperSend hasn't classified this reply"><Pill hue="slate" sz="sm">unread</Pill></span>}
                    {st && <Pill hue={st.hue} sz="sm">{st.label}</Pill>}
                    {!st && canEdit && r.contact_id && (
                      <span title="Adds them to the event as registered. Their company takes this event as its lead source if it has none.">
                        <Btn size="xs" variant={r.interest === 'interested' ? 'accent' : 'outline'} disabled={busy === r.contact_id}
                          onClick={() => register(r)}>{busy === r.contact_id ? 'Registering…' : 'Register'}</Btn>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!data.replies.length && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>No replies yet.</div>}
        </>
      )}
      {linking && ReactDOM.createPortal(
        <LeadEventCampaignDrawer event={event} onClose={() => setLinking(false)}
          onLinked={() => { reload(); onChanged && onChanged(); }} />, document.body)}
    </div>
  );
};

// ── Event drawer (create / edit) ───────────────────────────────────────────
// Opened from the Events page and inline from the event picker's "New event…". The
// event's dates are ITS dates; the lead date on a company is separate.
// presetCampaign ({ campaign_id, name }): a new event made from a detected
// SuperSend campaign — the campaign is linked as its invite right after create.
const LeadEventDrawer = ({ event, isNew, presetSourceId, presetName, presetCampaign, canRetire = false, canEdit = true, onClose, onSaved, onRetired, zIndex }) => {
  const [sources] = useLeadSources();
  const [form, setForm] = React.useState({
    name: event?.name || presetName || '',
    lead_source_id: event?.lead_source_id || presetSourceId || '',
    starts_on: event?.starts_on || '',
    ends_on: event?.ends_on || '',
    city: event?.city || '',
    state: event?.state || '',
    notes: event?.notes || '',
  });
  const [host, setHost] = React.useState(event?.host_contact_id
    ? { id: event.host_contact_id, full_name: event.host_contact_name, company_name: event.host_company_name }
    : null);
  const [detail, setDetail] = React.useState(null);   // funnel + attendees (edit only)
  const [saving, setSaving] = React.useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reloadDetail = React.useCallback(() => {
    if (isNew || !event?.id) return;
    window.AtlasApi.getLeadEvent(event.id).then(setDetail).catch(() => setDetail(null));
  }, [isNew, event?.id]);
  React.useEffect(() => { reloadDetail(); }, [reloadDetail]);

  const save = async () => {
    if (!form.name.trim()) { pushToast({ type: 'error', title: 'Give the event a name' }); return; }
    if (!form.lead_source_id) { pushToast({ type: 'error', title: 'Pick the channel', body: 'Workshop, Lunch and Learn, webinar… the event belongs to one.' }); return; }
    const payload = {
      name: form.name.trim(),
      lead_source_id: form.lead_source_id,
      starts_on: form.starts_on || null,
      ends_on: form.ends_on || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      host_contact_id: host?.id || null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      const saved = isNew
        ? await window.AtlasApi.createLeadEvent(payload)
        : await window.AtlasApi.updateLeadEvent(event.id, payload);
      let linked = null;
      if (isNew && presetCampaign) {
        linked = await window.AtlasApi.linkLeadEventCampaign(saved.id, { campaign_id: presetCampaign.campaign_id, campaign_name: presetCampaign.name })
          .catch((e) => { pushToast({ type: 'error', title: 'Event created, but the campaign did not link', body: e.message }); return null; });
      }
      pushToast({ type: 'success', title: isNew ? 'Event created' : 'Event saved',
                  body: linked ? `${saved.name} · ${linked.invited.toLocaleString()} invited` : saved.name });
      onSaved && onSaved(saved);
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  const retire = async () => {
    const ok = await showConfirm({
      title: `Retire ${event.name}?`,
      body: 'Companies and deals that came from it keep it as their lead source; it just stops showing in pickers.',
      confirmLabel: 'Retire', danger: true,
    });
    if (!ok) return;
    try {
      await window.AtlasApi.deleteLeadEvent(event.id);
      pushToast({ type: 'success', title: 'Event retired' });
      onRetired && onRetired();
      onClose();
    } catch (e) { pushToast({ type: 'error', title: 'Could not retire', body: e.message }); }
  };

  const Stat = ({ label, value }) => (
    <div style={{ flex: 1, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }} className="num">{value}</div>
      <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );

  return (
    <Drawer open onClose={onClose} zIndex={zIndex}
      title={isNew ? 'New event' : event.name}
      subtitle={isNew
        ? undefined
        : [event.lead_source_name, eventDateLabel(event), eventPlace(event)].filter(Boolean).join(' · ')}
      footer={(
        <>
          {!isNew && canRetire && event.is_active && <Btn size="sm" variant="ghost" onClick={retire} disabled={saving}>Retire</Btn>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}</Btn>
        </>
      )}
    >
      <Field label="Name"><TextInput value={form.name} onChange={set('name')} placeholder="e.g. Phoenix Lunch and Learn" autoFocus /></Field>
      <Field label="Channel" hint="Every event belongs to one channel; companies that come from this event get the channel too.">
        <Select value={form.lead_source_id} onChange={set('lead_source_id')}
          options={[
            { value: '', label: '— pick a channel —' },
            ...sources.filter((s) => s.is_active || s.id === form.lead_source_id).map((s) => ({ value: s.id, label: s.name })),
          ]} />
      </Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><Field label="Starts"><TextInput type="date" value={form.starts_on} onChange={set('starts_on')} /></Field></div>
        <div style={{ flex: 1 }}><Field label="Ends" hint="Only for multi-day events."><TextInput type="date" value={form.ends_on} onChange={set('ends_on')} /></Field></div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><Field label="City"><TextInput list={window.GEO?.LIST_IDS?.city} value={form.city} onChange={set('city')} placeholder="e.g. Phoenix" /></Field></div>
        <div style={{ flex: 1 }}><Field label="State"><TextInput list={window.GEO?.LIST_IDS?.state} value={form.state} onChange={set('state')} placeholder="e.g. Arizona" /></Field></div>
      </div>
      <Field label="Hosted by" hint="The referral partner who hosted or co-hosted — a contact.">
        <window.ContactPicker value={host} onPick={setHost} placeholder="Search contacts…" createType="professional" />
      </Field>
      <Field label="Notes">
        <textarea value={form.notes} onChange={set('notes')} rows={3} style={{
          width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)',
          borderRadius: 5, fontFamily: 'inherit', resize: 'vertical', minHeight: 50,
        }} />
      </Field>

      {isNew && presetCampaign && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8 }}>
          <Icon.Mail size={10} /> Links <b>{presetCampaign.name}</b> as its invite campaign.
        </div>
      )}

      {!isNew && event?.id && <LeadEventInvites event={event} canEdit={canEdit} onChanged={reloadDetail} />}

      {detail && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>What it fed</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Stat label="In the room" value={detail.attendee_count} />
            <Stat label="Companies" value={detail.company_count} />
            <Stat label="Deals" value={detail.engagement_count} />
            <Stat label="Won" value={detail.won_count} />
          </div>
          {detail.attendees.length > 0 && (
            <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {detail.attendees.map((a) => (
                <div key={a.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span role="button" style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 500 }}
                    onClick={() => window.AtlasNav?.goToContact && window.AtlasNav.goToContact(a.contact_id)}>{a.contact_name || 'Contact'}</span>
                  {a.company_name && <span style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {a.company_name}</span>}
                  <div style={{ flex: 1 }} />
                  {a.invited_by_name && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>brought by {a.invited_by_name}</span>}
                  <Pill hue={(ATTENDEE_STATUS_META[a.status] || ATTENDEE_STATUS_META.attended).hue} sz="sm">{(ATTENDEE_STATUS_META[a.status] || {}).label || a.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

// ── Contact: the Events panel ──────────────────────────────────────────────
// Recording an attendance is the capture point. It can stamp the contact's
// company with the event as its lead source and record the inviter as the
// contact's referrer — both only when nothing is recorded yet.
const AttendanceDrawer = ({ contact, onClose, onSaved }) => {
  const raw = contact._raw || {};
  const [eventId, setEventId] = React.useState('');
  const [eventLabel, setEventLabel] = React.useState('');
  const [status, setStatus] = React.useState('attended');
  const [inviter, setInviter] = React.useState(null);
  const [recordReferral, setRecordReferral] = React.useState(true);
  const [stampCompany, setStampCompany] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const hasReferral = !!(raw.referral_source_kind || raw.referral_source_contact_id || raw.referral_maker);
  // The company's current lead source decides whether "stamp the company" is
  // an offer or a no-op — first touch sticks, so an existing one won't move.
  const [companyLead, setCompanyLead] = React.useState(null);   // { name, source } | null
  React.useEffect(() => {
    if (!raw.company_id) return;
    window.AtlasApi.getCompany(raw.company_id)
      .then((co) => setCompanyLead({ name: co.name, source: co.lead_source_name || null }))
      .catch(() => setCompanyLead(null));
  }, [raw.company_id]);
  const companyHasSource = !!(companyLead && companyLead.source);

  const save = async () => {
    if (!eventId) { pushToast({ type: 'error', title: 'Pick the event' }); return; }
    setSaving(true);
    try {
      await window.AtlasApi.upsertLeadEventAttendee(eventId, contact.id, {
        status,
        invited_by_contact_id: inviter?.id || null,
        record_referral: !!inviter && recordReferral,
        stamp_company: stampCompany,
      });
      pushToast({ type: 'success', title: 'Attendance recorded', body: eventLabel || undefined });
      onSaved && onSaved();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not record', body: e.message });
    } finally { setSaving(false); }
  };

  const Check = ({ checked, onChange, children }) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', marginTop: 8 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <span>{children}</span>
    </label>
  );

  return (
    <Drawer open onClose={onClose} zIndex={60}
      title="Record an event"
      subtitle={contact.name}
      footer={(
        <>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving || !eventId}>{saving ? 'Saving…' : 'Record'}</Btn>
        </>
      )}
    >
      <Field label="Event" hint="Can't find it? Pick “New event…” at the top of the list.">
        <LeadEventPicker
          value={eventId}
          valueLabel={eventLabel}
          onChange={(v, label) => { setEventId(v || ''); setEventLabel(label || ''); }}
          onCreateNew={() => setCreating(true)}
        />
      </Field>
      <Field label="Status">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={ATTENDEE_STATUS_OPTIONS} />
      </Field>
      <Field label="Brought by" hint="The referral partner who invited them, if any.">
        <window.ContactPicker value={inviter} onPick={setInviter} placeholder="Search contacts…" createType="professional" />
      </Field>
      {inviter && !hasReferral && (
        <Check checked={recordReferral} onChange={setRecordReferral}>
          Also record <b>{inviter.full_name}</b> as who referred {contact.name} — it shows on the Referrals grid.
        </Check>
      )}
      {inviter && hasReferral && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8 }}>
          {contact.name} already has a referrer recorded, so this won't change the Referral panel.
        </div>
      )}
      {raw.company_id && companyLead && !companyHasSource && (
        <Check checked={stampCompany} onChange={setStampCompany}>
          Set <b>{companyLead.name || raw.company_name || 'their company'}</b>'s lead source from this event (it has none recorded). Its deals inherit it.
        </Check>
      )}
      {raw.company_id && companyHasSource && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 8 }}>
          {companyLead.name} already came in via <b>{companyLead.source}</b> — first touch sticks, so this event won't replace it.
        </div>
      )}
      {creating && ReactDOM.createPortal(
        <LeadEventDrawer
          isNew
          onClose={() => setCreating(false)}
          onSaved={(ev) => { setCreating(false); setEventId(ev.id); setEventLabel(ev.name); }}
          zIndex={70}
        />, document.body)}
    </Drawer>
  );
};

// The contact page passes optional (render only once the person has an
// event — the drawer still opens on createTick, a counter the header's ⋯
// menu bumps), onPresence(bool) once the rows are known, and cta=false to
// drop "Record event" (merged duplicates). The phone page passes none.
// readOnly hides the per-row × (the v2 Programs drawer for a viewer or a merged duplicate).
const ContactLeadEventsPanel = ({ contact, activeUser, onChanged, optional = false, cta = true, createTick = 0, onPresence, readOnly = false }) => {
  const [rows, setRows] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const contactId = contact && contact.id;

  const reload = React.useCallback(() => {
    if (!contactId) return;
    window.AtlasApi.listContactLeadEvents(contactId)
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, [contactId]);
  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    if (rows !== null && onPresence) onPresence(rows.length > 0);
  }, [rows]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Only a change after mount opens the drawer — a remount with an old
  // count stays shut.
  const seenTick = React.useRef(createTick);
  React.useEffect(() => {
    if (createTick && createTick !== seenTick.current) { seenTick.current = createTick; setAdding(true); }
  }, [createTick]);

  const remove = async (a) => {
    const ok = await showConfirm({
      title: `Remove ${contact.name} from ${a.event_name}?`,
      body: 'Only the attendance goes. If the company took its lead source from this event, that stays — correct it on the company page.',
      confirmLabel: 'Remove', danger: true,
    });
    if (!ok) return;
    try {
      await window.AtlasApi.removeLeadEventAttendee(a.event_id, contact.id);
      reload();
    } catch (e) { pushToast({ type: 'error', title: 'Could not remove', body: e.message }); }
  };

  const drawer = adding && (
    <AttendanceDrawer
      contact={contact}
      onClose={() => setAdding(false)}
      onSaved={() => { setAdding(false); reload(); onChanged && onChanged(); }}
    />
  );
  if (optional && !(rows && rows.length)) return drawer || null;

  return (
    <Section
      title="Events"
      action={cta ? (
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
          title="Record a workshop, lunch-and-learn or webinar this person was at"
          onClick={() => setAdding(true)}>Record event</Btn>
      ) : null}
    >
      {rows === null && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
          No events recorded.
        </div>
      )}
      {rows && rows.length > 0 && (
        <div>
          {rows.map((a) => (
            <div key={a.event_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text)' }}>{a.event_name}</span>
                  {a.lead_source_name && <Pill hue="violet" sz="sm">{a.lead_source_name}</Pill>}
                  <Pill hue={(ATTENDEE_STATUS_META[a.status] || ATTENDEE_STATUS_META.attended).hue} sz="sm">{(ATTENDEE_STATUS_META[a.status] || {}).label || a.status}</Pill>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {a.starts_on && <span>{fmtLeadDate(a.starts_on)}</span>}
                  {a.invited_by_name && (
                    <span>· brought by <span role="button" style={{ cursor: 'pointer', color: 'var(--accent-text)' }}
                      onClick={() => window.AtlasNav?.goToContact && window.AtlasNav.goToContact(a.invited_by_contact_id)}>{a.invited_by_name}</span></span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <button onClick={() => remove(a)} title="Remove attendance"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 13, padding: '0 2px' }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {drawer}
    </Section>
  );
};

// ── Settings → Lead sources ────────────────────────────────────────────────
const LeadSourceDrawer = ({ source, isNew, onClose, onChange }) => {
  const [form, setForm] = React.useState({
    name: source?.name || '',
    source_group: source?.source_group || 'event',
    dc_source_type: source?.dc_source_type || '',
    sort_order: source?.sort_order ?? 100,
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { pushToast({ type: 'error', title: 'Give the channel a name' }); return; }
    const payload = {
      name: form.name.trim(),
      source_group: form.source_group,
      dc_source_type: form.dc_source_type.trim() || null,
      sort_order: Number(form.sort_order) || 100,
    };
    setSaving(true);
    try {
      if (isNew) await window.AtlasApi.createLeadSource(payload);
      else await window.AtlasApi.updateLeadSource(source.id, payload);
      pushToast({ type: 'success', title: isNew ? 'Lead source created' : 'Lead source saved' });
      onChange(); onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  const toggleActive = async () => {
    if (source.is_active) {
      const ok = await showConfirm({
        title: `Retire ${source.name}?`,
        body: `${source.company_count || 0} companies and ${source.engagement_count || 0} deals keep it as their lead source; it just stops showing in pickers.`,
        confirmLabel: 'Retire', danger: true,
      });
      if (!ok) return;
      await window.AtlasApi.deleteLeadSource(source.id);
      pushToast({ type: 'success', title: 'Lead source retired' });
    } else {
      await window.AtlasApi.updateLeadSource(source.id, { is_active: true });
      pushToast({ type: 'success', title: 'Lead source restored' });
    }
    onChange(); onClose();
  };

  return (
    <Drawer open onClose={onClose}
      title={isNew ? 'New lead source' : source.name}
      subtitle={isNew ? undefined : `${(LEAD_GROUP_META[source.source_group] || {}).label || source.source_group} channel`}
      footer={(
        <>
          {!isNew && <Btn size="sm" variant="ghost" onClick={toggleActive} disabled={saving}>{source.is_active ? 'Retire' : 'Restore'}</Btn>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}</Btn>
        </>
      )}
    >
      <Field label="Name"><TextInput value={form.name} onChange={set('name')} placeholder="e.g. Lunch and Learn" autoFocus /></Field>
      <Field label="Group" hint="The coarse report dimension. Referral = a person brought them; Event = we met them somewhere; Inbound = they found us; Outbound = we found them.">
        <Select value={form.source_group} onChange={set('source_group')} options={LEAD_GROUP_OPTIONS} />
      </Field>
      <Field label="DealCloud value" hint="The DealCloud SourceType this channel corresponds to, if DC has one (e.g. “Trade Show”). Leave empty when it doesn't.">
        <TextInput value={form.dc_source_type} onChange={set('dc_source_type')} placeholder="(none)" />
      </Field>
      <Field label="Sort order" hint="Lower shows first in pickers."><TextInput type="number" value={form.sort_order} onChange={set('sort_order')} /></Field>
    </Drawer>
  );
};

const LeadSourcesSettings = () => {
  const [sources, setSources] = React.useState(null);
  const [showRetired, setShowRetired] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(async () => {
    const s = await window.AtlasApi.listLeadSources({ include_inactive: true, with_counts: true }).catch(() => []);
    setSources(Array.isArray(s) ? s : []);
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  if (sources === null) return <SettingsHeader title="Lead sources" sub="Loading…" />;

  const visibleSources = sources.filter((s) => showRetired || s.is_active);
  const head = { display: 'flex', height: 30, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' };
  const cell = (w, extra) => ({ width: w, padding: '0 10px', display: 'flex', alignItems: 'center', ...(extra || {}) });
  const num = (w) => cell(w, { justifyContent: 'flex-end' });
  const rowStyle = (active) => ({ display: 'flex', minHeight: 38, alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--border)', opacity: active ? 1 : 0.5 });
  const hover = { onMouseEnter: (e) => e.currentTarget.style.background = 'var(--surface-2)', onMouseLeave: (e) => e.currentTarget.style.background = 'transparent' };

  return (
    <>
      <SettingsHeader
        title="Lead sources"
        action={(
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> show retired
            </label>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>New lead source</Btn>
          </div>
        )}
      />
      <div style={{ padding: '20px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          <div style={head}>
            <div style={{ flex: 2, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Lead source</div>
            <div style={cell(110)}>Group</div>
            <div style={cell(220)}>DealCloud value</div>
            <div style={num(100)}>Companies</div>
            <div style={num(80)}>Deals</div>
            <div style={num(80)}>Events</div>
            <div style={cell(80)}>Status</div>
          </div>
          {visibleSources.map((s) => (
            <div key={s.id} onClick={() => setEditing(s)} style={rowStyle(s.is_active)} {...hover}>
              <div style={{ flex: 2, padding: '6px 14px', fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>{s.name}</div>
              <div style={cell(110)}><LeadSourcePill name={(LEAD_GROUP_META[s.source_group] || {}).label || s.source_group} group={s.source_group} /></div>
              <div style={{ ...cell(220), fontSize: 12, color: s.dc_source_type ? 'var(--text-2)' : 'var(--text-4)' }}>{s.dc_source_type || 'none in DealCloud'}</div>
              <div style={{ ...num(100), fontSize: 12 }} className="num">{s.company_count}</div>
              <div style={{ ...num(80), fontSize: 12 }} className="num">{s.engagement_count}</div>
              <div style={{ ...num(80), fontSize: 12 }} className="num">{s.event_count}</div>
              <div style={cell(80)}>{s.is_active ? <Pill hue="green" dot sz="sm">active</Pill> : <Pill hue="slate" sz="sm">retired</Pill>}</div>
            </div>
          ))}
          {visibleSources.length === 0 && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>No lead sources yet.</div>}
        </div>
      </div>

      {(editing || creating) && (
        <LeadSourceDrawer source={editing} isNew={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onChange={reload} />
      )}
    </>
  );
};

// ── Events view (sidebar) ──────────────────────────────────────────────────
// Events are operational records — the workshop a dealmaker ran last week,
// the campaign marketing is running now — created weekly and browsed for
// "what came of it". That is a sidebar page, not a Settings pane: Settings
// is where configuration lives (the channel catalog above), and an admin-
// flavoured door in front of the events table is the wrong signal to the
// people who should be adding to it. Retiring an event stays admin-only.
// SuperSend campaigns whose names read as events (Lunch and Learn, Workshop,
// Webinar…) that no event claims yet. "Create event" makes the event with
// the campaign linked as its invite; "Not an event" stops the suggestion.
const EventCampaignSuggestions = ({ canEdit, refreshKey, onCreate }) => {
  const [items, setItems] = React.useState([]);
  const reload = React.useCallback(() => {
    window.AtlasApi.leadEventCampaignSuggestions()
      .then((d) => setItems((d && d.items) || []))
      .catch(() => setItems([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload, refreshKey]);
  const dismiss = async (c) => {
    try {
      await window.AtlasApi.dismissLeadEventCampaign(c.campaign_id);
      setItems((xs) => xs.filter((x) => x.campaign_id !== c.campaign_id));
    } catch (e) { pushToast({ type: 'error', title: 'Could not dismiss', body: e.message }); }
  };
  if (!items.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text)', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon.Mail size={11} />
        <span title="Recently sending SuperSend campaigns whose names read as events, with no event linked yet.">
          {items.length} SuperSend campaign{items.length === 1 ? '' : 's'} look{items.length === 1 ? 's' : ''} like an event
        </span>
      </div>
      {items.map((c) => (
        <div key={c.campaign_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              {c.recipients.toLocaleString()} invited · sending {fmtLeadDate(c.first_at)} – {fmtLeadDate(c.last_at)}
            </div>
          </div>
          {c.lead_source_name && <LeadSourcePill name={c.lead_source_name} group="event" />}
          {canEdit && <Btn size="xs" variant="ghost" onClick={() => dismiss(c)}>Not an event</Btn>}
          {canEdit && <Btn size="xs" variant="accent" onClick={() => onCreate(c)}>Create event</Btn>}
        </div>
      ))}
    </div>
  );
};

const LeadEventsView = ({ activeUser, currentRole, isAdmin = false, setContactId }) => {
  const [events, setEvents] = React.useState(null);
  const [sources] = useLeadSources();
  const [q, setQ] = React.useState('');
  const [channelId, setChannelId] = React.useState('');
  const [year, setYear] = React.useState('');
  const [showRetired, setShowRetired] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [fromCampaign, setFromCampaign] = React.useState(null);   // a detected campaign → new event
  const [refreshKey, setRefreshKey] = React.useState(0);
  // Viewers browse; editors and admins add. Same line the API draws
  // (POST /lead-events and Register need edit_records). currentRole is the
  // AUTH role (admin/editor/viewer); activeUser.role is the team role
  // (senior_associate, dealmaker…) and never matched, which hid "New event"
  // from every dealmaker.
  const authRole = (currentRole || '').toLowerCase();
  const canEdit = authRole ? ['admin', 'editor'].includes(authRole) : true;

  const reload = React.useCallback(() => {
    // The API caps a page at 500; the whole catalog fits for a long while,
    // so filtering is client-side and instant.
    window.AtlasApi.listLeadEvents({ include_inactive: true, limit: 500 })
      .then((ev) => setEvents(Array.isArray(ev) ? ev : []))
      .catch(() => setEvents([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const all = events || [];
  const years = React.useMemo(() => {
    const ys = new Set(all.filter((e) => e.starts_on).map((e) => String(e.starts_on).slice(0, 4)));
    return [...ys].sort().reverse();
  }, [all]);
  const needle = q.trim().toLowerCase();
  const rows = all
    .filter((e) => showRetired || e.is_active)
    .filter((e) => !channelId || e.lead_source_id === channelId)
    .filter((e) => !year || (year === 'undated' ? !e.starts_on : String(e.starts_on || '').startsWith(year)))
    .filter((e) => !needle || [e.name, e.city, e.state, e.host_contact_name, e.lead_source_name]
      .some((v) => (v || '').toLowerCase().includes(needle)))
    // Newest first; undated events (most of the backfilled history) sink to
    // the bottom rather than masquerading as current.
    .sort((a, b) => (a.starts_on && b.starts_on ? String(b.starts_on).localeCompare(String(a.starts_on))
      : a.starts_on ? -1 : b.starts_on ? 1 : (a.name || '').localeCompare(b.name || '')));

  const head = { display: 'flex', height: 30, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' };
  // Fixed cells never shrink and the Event column keeps EVENT_MIN, so a
  // narrow window (an iPad beside the sidebar) scrolls the table sideways
  // instead of squeezing event names down to "Bui…". On a wide window the
  // Event column still takes all the spare room, exactly as before. 150
  // keeps a 1280px laptop with the sidebar (Event ≈ 160–175px today, even
  // with a classic scrollbar) pixel-identical; only narrower windows scroll.
  const EVENT_MIN = 150;
  const cell = (w, extra) => ({ width: w, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center', ...(extra || {}) });
  const num = (w) => cell(w, { justifyContent: 'flex-end' });
  const tableMin = EVENT_MIN + 150 + 130 + 170 + 80 + 90 + 100 + 70 + 60;   // Event + the fixed cells below
  const rowStyle = (active) => ({ display: 'flex', minHeight: 38, alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--border)', opacity: active ? 1 : 0.5 });
  const hover = { onMouseEnter: (e) => e.currentTarget.style.background = 'var(--surface-2)', onMouseLeave: (e) => e.currentTarget.style.background = 'transparent' };
  const inRoom = rows.reduce((n, e) => n + (e.attendee_count || 0), 0);
  const deals = rows.reduce((n, e) => n + (e.engagement_count || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Events']}
        title="Events"
        actions={(
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, places, hosts…"
              style={{ width: 240, padding: '4px 10px', fontSize: 12,
                       border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit' }} />
            {canEdit && (
              <Btn size="sm" variant="accent" icon={<Icon.Plus size={11} />} onClick={() => setCreating(true)}>
                New event
              </Btn>
            )}
          </>
        )}
      />

      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        <EventCampaignSuggestions canEdit={canEdit} refreshKey={refreshKey}
          onCreate={(c) => { setFromCampaign(c); setCreating(true); }} />
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: 10, marginBottom: 14,
                      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Channel:</span>
            <Combobox value={channelId} onChange={(e) => setChannelId(e.target.value)}
              options={[{ value: '', label: 'Any channel' },
                        ...sources.filter((s) => s.is_active || s.id === channelId).map((s) => ({ value: s.id, label: s.name }))]}
              style={{ minWidth: 180 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Year:</span>
            <Combobox value={year} onChange={(e) => setYear(e.target.value)}
              options={[{ value: '', label: 'Any year' },
                        ...years.map((y) => ({ value: y, label: y })),
                        { value: 'undated', label: 'Undated' }]}
              style={{ minWidth: 120 }} />
          </div>
          <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> show retired
          </label>
          <div style={{ flex: 1 }} />
          {events !== null && (
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {rows.length} event{rows.length === 1 ? '' : 's'} · {inRoom.toLocaleString()} in the room · {deals} deal{deals === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflowX: 'auto' }}>
          <div style={{ minWidth: tableMin }}>
          <div style={head}>
            <div style={{ flex: 2, minWidth: EVENT_MIN, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Event</div>
            <div style={cell(150)}>Channel</div>
            <div style={cell(130)}>Date</div>
            <div style={cell(170)}>Hosted by</div>
            <div style={num(80)} title="People its SuperSend invite campaign emailed">Invited</div>
            <div style={num(90)}>In room</div>
            <div style={num(100)}>Companies</div>
            <div style={num(70)}>Deals</div>
            <div style={num(60)}>Won</div>
          </div>
          {events === null && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
          {rows.map((ev) => (
            <div key={ev.id} onClick={() => setEditing(ev)} style={rowStyle(ev.is_active)} {...hover}>
              <div style={{ flex: 2, padding: '6px 14px', minWidth: EVENT_MIN }}>
                <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</div>
                {eventPlace(ev) && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{eventPlace(ev)}</div>}
              </div>
              <div style={cell(150)}>{ev.lead_source_name && <LeadSourcePill name={ev.lead_source_name} group={ev.lead_source_group} />}</div>
              <div style={{ ...cell(130), fontSize: 12, color: ev.starts_on ? 'var(--text-2)' : 'var(--text-4)' }}>{eventDateLabel(ev) || 'undated'}</div>
              <div style={{ ...cell(170), fontSize: 12, color: ev.host_contact_name ? 'var(--text-2)' : 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ev.host_contact_name && ev.host_contact_id && setContactId ? (
                  <a onClick={(e) => { e.stopPropagation(); setContactId(ev.host_contact_id); }}
                    style={{ color: 'var(--accent-text)', cursor: 'pointer' }}>{ev.host_contact_name}</a>
                ) : (ev.host_contact_name || '—')}
              </div>
              <div style={{ ...num(80), fontSize: 12, color: ev.invited_count == null ? 'var(--text-4)' : undefined }} className="num"
                title={ev.invited_count == null ? 'No invite campaign linked' : undefined}>
                {ev.invited_count == null ? '—' : ev.invited_count.toLocaleString()}
              </div>
              <div style={{ ...num(90), fontSize: 12 }} className="num">{ev.attendee_count}</div>
              <div style={{ ...num(100), fontSize: 12 }} className="num">{ev.company_count}</div>
              <div style={{ ...num(70), fontSize: 12 }} className="num">{ev.engagement_count}</div>
              <div style={{ ...num(60), fontSize: 12 }} className="num">{ev.won_count}</div>
            </div>
          ))}
          {events !== null && rows.length === 0 && (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>
              {all.length === 0
                ? 'No events yet. Create one here, or from any company, deal or contact page.'
                : 'No events match these filters.'}
            </div>
          )}
          </div>
        </div>
      </div>

      {(editing || creating) && (
        <LeadEventDrawer event={editing} isNew={creating} canRetire={isAdmin} canEdit={canEdit}
          presetName={fromCampaign?.name} presetSourceId={fromCampaign?.lead_source_id || undefined}
          presetCampaign={fromCampaign ? { campaign_id: fromCampaign.campaign_id, name: fromCampaign.name } : null}
          onClose={() => { setEditing(null); setCreating(false); setFromCampaign(null); reload(); setRefreshKey((k) => k + 1); }}
          onSaved={() => { setEditing(null); setCreating(false); setFromCampaign(null); reload(); setRefreshKey((k) => k + 1); }}
          onRetired={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
};

// ── Composer nudge ─────────────────────────────────────────────────────────
// The note composer is where the first call gets logged, and the first call
// is when the dealmaker still knows where the lead came from. While the
// attached company has no source, the composers show one extra row; the pick
// is saved right after the note. Fill-only by construction: the row only
// appears while the field is blank, so it can never overwrite a recorded
// source — and it disappears the moment the company knows its origin.
const EMPTY_LEAD_NUDGE = { lead_source_id: '', lead_event_id: null, lead_event_name: '' };

const LeadSourceNudge = ({ companyId, value, onChange }) => {
  const [missing, setMissing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [sources] = useLeadSources();
  React.useEffect(() => {
    setMissing(false);
    if (!companyId) return undefined;
    let alive = true;
    window.AtlasApi.getCompany(companyId)
      .then((c) => { if (alive) setMissing(!!c && !c.lead_source_id); })
      .catch(() => { if (alive) setMissing(false); });
    return () => { alive = false; };
  }, [companyId]);
  if (!companyId || !missing) return null;
  const v = value || EMPTY_LEAD_NUDGE;
  const options = [
    { value: '', label: '— not recorded —' },
    ...sources.filter((s) => s.is_active).map((s) => ({
      value: s.id, label: s.name,
      hint: (LEAD_GROUP_META[s.source_group] || LEAD_GROUP_META.other).label,
    })),
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}
        title="This company has no lead source yet. Pick one and it saves with the note. It only fills the blank — a recorded source is never overwritten.">
        Lead source <span style={{ color: 'var(--text-4)' }}>· not recorded yet</span>
      </span>
      <div style={{ width: 190 }}>
        <Combobox value={v.lead_source_id || ''} searchable options={options}
          onChange={(e) => onChange({ ...EMPTY_LEAD_NUDGE, lead_source_id: e.target.value || '' })} />
      </div>
      {v.lead_source_id && (
        <div style={{ flex: 1, minWidth: 180 }}>
          <LeadEventPicker
            value={v.lead_event_id || ''}
            valueLabel={v.lead_event_name}
            sourceId={v.lead_source_id}
            onChange={(id, label) => onChange({ ...v, lead_event_id: id, lead_event_name: label })}
            onCreateNew={() => setCreating(true)}
          />
        </div>
      )}
      {creating && ReactDOM.createPortal(
        <LeadEventDrawer
          isNew
          presetSourceId={v.lead_source_id || null}
          onClose={() => setCreating(false)}
          onSaved={(ev) => {
            setCreating(false);
            onChange({ lead_source_id: ev.lead_source_id, lead_event_id: ev.id, lead_event_name: ev.name });
          }}
          zIndex={70}
        />, document.body)}
    </div>
  );
};

// Save the nudge's pick once the note itself has saved. Returns true when the
// company was stamped, so the caller can refresh what it shows. The server
// applies the rules (event wins, lead date = today) — this only sends the pick.
const saveLeadNudge = async (companyId, value) => {
  if (!companyId || !value || !value.lead_source_id) return false;
  await window.AtlasApi.updateCompany(companyId, {
    lead_source_id: value.lead_source_id,
    lead_event_id: value.lead_event_id || null,
  });
  return true;
};

Object.assign(window, {
  LeadSourcePill, LeadSourceFields, LeadEventDrawer, ContactLeadEventsPanel, LeadSourcesSettings,
  LeadEventsView, LeadSourceNudge, saveLeadNudge,
});
